import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '../store';

type AdminView = {
  userView: string[];
  schoolView: string[];
  schoolRosterView: string[];
  conferenceView: string[];
  invitelinkView: string[];
  power10kView: string[];
  division1View: string[];
  powerFiveSchoolView: string[];
  userRewardView: string[];
  rewardView: string[];
  rewardprizeView: string[];
  expertNetworkView: string[];
  expertCategoryView: string[];
  expertSubcategoryView: string[];
  aiPromptView: string[];
  surveyView: string[];
  sideMenu: number[];
};

const initialState: AdminView = {
  userView: [],
  schoolView: [],
  schoolRosterView: [],
  conferenceView: [],
  invitelinkView: [],
  power10kView: [],
  division1View: [],
  powerFiveSchoolView: [],
  userRewardView: [],
  rewardView: [],
  rewardprizeView: [],
  expertNetworkView: [],
  expertCategoryView: [],
  expertSubcategoryView: [],
  aiPromptView: [],
  surveyView: [],
  sideMenu: [],
};

const setup = createSlice({
  name: 'adminView',
  initialState,
  reducers: {
    setUserView(state, { payload }: PayloadAction<AdminView['userView']>) {
      return { ...state, userView: payload };
    },
    setSchoolView(state, { payload }: PayloadAction<AdminView['schoolView']>) {
      return { ...state, schoolView: payload };
    },
    setSchoolRosterView(
      state,
      { payload }: PayloadAction<AdminView['schoolRosterView']>
    ) {
      return { ...state, schoolRosterView: payload };
    },
    setConferenceView(
      state,
      { payload }: PayloadAction<AdminView['conferenceView']>
    ) {
      return { ...state, conferenceView: payload };
    },
    setInviteLinkView(
      state,
      { payload }: PayloadAction<AdminView['invitelinkView']>
    ) {
      return { ...state, invitelinkView: payload };
    },
    setPower10kView(
      state,
      { payload }: PayloadAction<AdminView['power10kView']>
    ) {
      return { ...state, power10kView: payload };
    },
    setDivision1View(
      state,
      { payload }: PayloadAction<AdminView['division1View']>
    ) {
      return { ...state, division1View: payload };
    },
    setPowerFiveSchoolView(
      state,
      { payload }: PayloadAction<AdminView['powerFiveSchoolView']>
    ) {
      return { ...state, powerFiveSchoolView: payload };
    },
    setUserRewardView(
      state,
      { payload }: PayloadAction<AdminView['userRewardView']>
    ) {
      return { ...state, userRewardView: payload };
    },
    setRewardView(state, { payload }: PayloadAction<AdminView['rewardView']>) {
      return { ...state, rewardView: payload };
    },
    setRewardPrizeView(
      state,
      { payload }: PayloadAction<AdminView['rewardprizeView']>
    ) {
      return { ...state, rewardprizeView: payload };
    },
    setExpertNetworkView(
      state,
      { payload }: PayloadAction<AdminView['expertNetworkView']>
    ) {
      return { ...state, expertNetworkView: payload };
    },
    setExpertCategoryView(
      state,
      { payload }: PayloadAction<AdminView['expertCategoryView']>
    ) {
      return { ...state, expertCategoryView: payload };
    },
    setExpertSubcategoryView(
      state,
      { payload }: PayloadAction<AdminView['expertSubcategoryView']>
    ) {
      return { ...state, expertSubcategoryView: payload };
    },
    setAiPromptView(
      state,
      { payload }: PayloadAction<AdminView['aiPromptView']>
    ) {
      return { ...state, aiPromptView: payload };
    },
    setSurveyView(
      state,
      { payload }: PayloadAction<AdminView['surveyView']>
    ) {
      return { ...state, surveyView: payload };
    },

    setSideMenu(state, { payload }: PayloadAction<AdminView['sideMenu']>) {
      return { ...state, sideMenu: payload };
    },
    clearViews() {
      return initialState;
    },
  },
});

export const {
  setUserView,
  setSchoolView,
  setSchoolRosterView,
  setConferenceView,
  setInviteLinkView,
  setPower10kView,
  setDivision1View,
  setPowerFiveSchoolView,
  setUserRewardView,
  setRewardView,
  setRewardPrizeView,
  setExpertNetworkView,
  setExpertCategoryView,
  setExpertSubcategoryView,
  setAiPromptView,
  setSurveyView,
  setSideMenu,
  clearViews,
} = setup.actions;

export const selectUserView = ({ adminView }: RootState) => adminView.userView;
export const selectSchoolView = ({ adminView }: RootState) =>
  adminView.schoolView;
export const selectSchoolRosterView = ({ adminView }: RootState) =>
  adminView.schoolRosterView;
export const selectConferenceView = ({ adminView }: RootState) =>
  adminView.conferenceView;
export const selectInviteLinkView = ({ adminView }: RootState) =>
  adminView.invitelinkView;
export const selectPower10kView = ({ adminView }: RootState) =>
  adminView.power10kView;
export const selectDivision1View = ({ adminView }: RootState) =>
  adminView.division1View;
export const selectPowerFiveSchoolView = ({ adminView }: RootState) =>
  adminView.powerFiveSchoolView;
export const selectUserRewardView = ({ adminView }: RootState) =>
  adminView.userRewardView;
export const selectRewardView = ({ adminView }: RootState) =>
  adminView.rewardView;
export const selectRewardPrizeView = ({ adminView }: RootState) =>
  adminView.rewardprizeView;
export const selectExpertNetworkView = ({ adminView }: RootState) =>
  adminView.expertNetworkView;
export const selectExpertCategoryView = ({ adminView }: RootState) =>
  adminView.expertCategoryView;
export const selectExpertSubcategoryView = ({ adminView }: RootState) =>
  adminView.expertSubcategoryView;
export const selectAiPromptView = ({ adminView }: RootState) =>
  adminView.aiPromptView;
export const selectSurveyView = ({ adminView }: RootState) =>
  adminView.surveyView;
export const selectSideMenu = ({ adminView }: RootState) => adminView.sideMenu;

export default setup.reducer;
